import api from './api'
import i18n from '../i18n'

export interface ClubAccess {
  uuid: string
  club_uuid: string
  type: 'FREE' | 'PAID'
  access_to: string
  next_check_payment_date: string
  next_amount: number | null
  currency: string
  hash: string
  active: boolean
  has_vat: boolean
  vat_rate: number
  created_at: string
  updated_at: string
}

export interface ClubAccessPayment {
  uuid: string
  club_uuid: string
  club_access_uuid: string
  provider: 'PAYU' | 'STRIPE'
  transaction_id: string | null
  payment_hash: string
  amount: number
  vat: number
  total_amount: number
  currency: string
  status: 'FREE' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  payer_email: string
  payer_name: string
  payment_url: string | null
  paid_at: string | null
  created_at: string
  updated_at: string
}

export interface Pagination {
  total: number
  per_page: number
  current_page: number
  last_page: number
}

export interface InitiateClubAccessPaymentDto {
  payer_email: string
  payer_name: string
  provider: 'PAYU' | 'STRIPE'
  return_url: string
  cancel_url: string
}

export interface InitiateClubAccessPaymentResponse {
  payment_uuid: string
  payment_hash: string
  payment_url: string
  transaction_id?: string
}

export interface PublicClubAccessPayment {
  uuid: string
  club_uuid: string
  club_name: string
  club_logo_url: string | null
  amount: number
  vat: number
  total_amount: number
  currency: string
  status: string
  payer_email: string
  payer_name: string
  payment_url: string | null
  paid_at: string | null
  created_at: string
  available_providers: string[]
}

/**
 * Club Access Service
 * Handles all club access payment-related API calls
 */
class ClubAccessService {
  /**
   * Get active club access
   * GET v1/{club}/access
   */
  async getClubAccess(clubUuid: string): Promise<{ club_access: ClubAccess }> {
    const response = await api.get(`v1/${clubUuid}/access`)
    return response.data
  }

  /**
   * Get club access payments list
   * GET v1/{club}/access/payments
   */
  async getAccessPayments(
    clubUuid: string,
    page: number = 1,
    perPage: number = 10
  ): Promise<{ payments: ClubAccessPayment[], pagination: Pagination }> {
    const response = await api.get(`v1/${clubUuid}/access/payments`, {
      params: { page, per_page: perPage }
    })
    return response.data
  }

  /**
   * Initiate club access payment
   * POST v1/{club}/access/payment/initiate
   */
  async initiatePayment(
    clubUuid: string,
    data: InitiateClubAccessPaymentDto
  ): Promise<InitiateClubAccessPaymentResponse> {
    const response = await api.post(`v1/${clubUuid}/access/payment/initiate`, data)
    return response.data
  }

  /**
   * Get club access payment status
   * GET v1/{club}/access/payment/{payment_hash}/status
   */
  async getPaymentStatus(clubUuid: string, paymentHash: string): Promise<{ payment: ClubAccessPayment }> {
    const response = await api.get(`v1/${clubUuid}/access/payment/${paymentHash}/status`)
    return response.data
  }

  /**
   * Get public club access payment details (no auth required)
   * GET v1/public/access-payment/{hash}
   */
  async getPublicPayment(paymentHash: string): Promise<{ payment: PublicClubAccessPayment }> {
    const response = await api.get(`v1/public/access-payment/${paymentHash}`)
    return response.data
  }

  /**
   * Initiate club access payment from public page (no auth required)
   * POST v1/public/access-payment/{hash}/initiate
   */
  async initiatePublicPayment(
    hash: string,
    data: InitiateClubAccessPaymentDto
  ): Promise<InitiateClubAccessPaymentResponse> {
    const response = await api.post(`v1/public/access-payment/${hash}/initiate`, data)
    return response.data
  }

  /**
   * Format amount with currency symbol
   * PLN: 49,99 zł | EUR: €11,99 | USD: $12,99
   */
  formatAmount(amount: number | null | undefined, currency: string = 'PLN'): string {
    if (amount === null || amount === undefined) {
      amount = 0
    }
    const formatted = Number(amount).toFixed(2)

    switch (currency) {
      case 'EUR':
        return `€${formatted}`
      case 'USD':
        return `$${formatted}`
      case 'GBP':
        return `£${formatted}`
      case 'PLN':
      default:
        return `${formatted} PLN`
    }
  }

  /**
   * Get status display text
   */
  getStatusText(status: string): string {
    const t = i18n.global.t as (key: string) => string
    const key = `clubAccessStatus.${status}`
    const translated = t(key)
    return translated !== key ? translated : status
  }

  /**
   * Get status color class
   */
  getStatusColorClass(status: string): string {
    const colorMap: Record<string, string> = {
      FREE: 'text-green-600 bg-green-50 border-green-200',
      PENDING: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      PROCESSING: 'text-blue-600 bg-blue-50 border-blue-200',
      COMPLETED: 'text-green-600 bg-green-50 border-green-200',
      FAILED: 'text-red-600 bg-red-50 border-red-200',
      CANCELLED: 'text-gray-600 bg-gray-50 border-gray-200'
    }
    return colorMap[status] || 'text-gray-600 bg-gray-50 border-gray-200'
  }

  /**
   * Calculate days remaining until access expires
   */
  getDaysRemaining(accessTo: string): number {
    const accessDate = new Date(accessTo)
    const today = new Date()
    const diffTime = accessDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  /**
   * Check if access is expiring soon (within 7 days)
   */
  isExpiringSoon(accessTo: string): boolean {
    const daysRemaining = this.getDaysRemaining(accessTo)
    return daysRemaining <= 7 && daysRemaining > 0
  }

  /**
   * Check if access has expired
   */
  isExpired(accessTo: string): boolean {
    const daysRemaining = this.getDaysRemaining(accessTo)
    return daysRemaining < 0
  }
}

export default new ClubAccessService()
